/**
 * The single iOS platform gate for "local mode" (signed-out use).
 *
 * Background (App Store submission d86a49f2-846b-44f2-84ab-68fa0df71b9a):
 * Apple rejected this app three times under Guideline 5.1.1(v) ("Data Collection
 * and Storage" / account-creation requirements) and, on the third round, explicitly
 * asked for the sign-in wall to be REMOVED, not softened — the app must be usable
 * without creating an account. Local mode is that removal: a signed-out user gets
 * a fully functional on-device budget (LOCAL-01..LOCAL-08), with an opt-in path to
 * create an account later and migrate their local data onto it (05.1-04).
 *
 * Why iOS-only: this repo ships ONE web bundle to web, iOS (Capacitor) and Android
 * (Capacitor). There is no separate iOS build. There are 11 real production users
 * on web/Android today with zero regression tolerance, and Apple's requirement is
 * iOS-specific (Android's Play Store has no equivalent policy driving this work).
 * So local mode must be reachable ONLY inside the iOS native shell — never on the
 * web origin, never inside the Android shell — even though every platform loads
 * the exact same JS bundle.
 *
 * This file is the ONE place in the codebase allowed to call
 * `Capacitor.getPlatform()` to decide whether local mode may exist at all.
 * Nothing else may duplicate this check.
 *
 * `setLocalModeActive` is called from exactly one place in the app:
 * `apps/web/src/routes/_app.tsx`'s `beforeLoad` (wired in 05.1-03). Until that
 * wave lands, nothing calls it, so `isLocalModeActive()` returns `false` for
 * every user on every platform and this module is a runtime no-op.
 */
import { Capacitor } from '@capacitor/core';
import { isRunningInNativeShell } from './pwa';

/**
 * Dev-only escape hatch: lets `isLocalModeActive()` be exercised on the Vite dev
 * server (e.g. on Windows, without Xcode) by loading the page with `?localmode`
 * in the URL. Captured ONCE at module init from `window.location.search` — the
 * same freeze-at-module-load reasoning as `pageLoadedAsOAuthCallback` in
 * `lib/auth.ts`: later navigations must not change the answer within this load.
 *
 * `import.meta.env.DEV` is statically `false` in every SHIPPED build (web, the
 * iOS shell, the Android shell) — Vite/rolldown substitutes the literal value at
 * build time and dead-code-eliminates this entire block, so no production bundle
 * (including the App Store binary) can ever reach this branch. `window` is
 * guarded because this module can, in principle, be evaluated outside a browser
 * (e.g. a future test runner).
 */
function devLocalModeQueryParamPresent(): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.has('localmode');
}

/**
 * Whether local mode can exist on this device at all.
 *
 * Explicitly typed `boolean` (not inferred `false`/`true`) so TypeScript keeps
 * both branches of every ternary that reads it live — mirrors
 * `GOOGLE_SSO_UI_ENABLED` in `routes/login.tsx`, for the same reason: this
 * file's tsconfig sets `noUnusedLocals`, and an inferred literal type would let
 * TypeScript narrow away the other branch.
 *
 * True when running inside the native shell on iOS specifically (mirrors the
 * exact guard in `lib/nativeScroll.ts`: native shell AND Capacitor platform
 * equal to 'ios'), OR when the dev-only escape hatch above is present.
 * Evaluated ONCE at module scope — the shell cannot change at
 * runtime, matching the house pattern (`const isNative = isRunningInNativeShell()`
 * at module scope in `routes/login.tsx`).
 */
export const LOCAL_MODE_PLATFORM_ENABLED: boolean =
  (isRunningInNativeShell() && Capacitor.getPlatform() === 'ios') ||
  devLocalModeQueryParamPresent();

// Module-private mutable flag. Starts false. Only `setLocalModeActive` may change it.
let localModeActive = false;

/**
 * Activate or deactivate local mode. Called from exactly one place:
 * `routes/_app.tsx`'s `beforeLoad` (05.1-03).
 *
 * The AND against `LOCAL_MODE_PLATFORM_ENABLED` is the load-bearing safety
 * property (T-05.1-06): even a bug that calls `setLocalModeActive(true)` on
 * web or Android cannot turn local mode on, because the platform gate above
 * is `false` there and `false && anything` is always `false`.
 */
export function setLocalModeActive(active: boolean): void {
  localModeActive = LOCAL_MODE_PLATFORM_ENABLED && active;
}

/** Whether local mode is active right now. Starts `false` for every user on every platform. */
export function isLocalModeActive(): boolean {
  return localModeActive;
}
